/* eslint-disable import/prefer-default-export */
import appointmentBookingConfig from '../../Data/Maps/appointmentBookingConfig';
import {
  areConditionalQuestionsAnswered,
  getPostcodeComponent,
} from '../__helpers__/component.helpers';

/**
 * Adds the specified number of minutes to a given ISO 8601 string (that potentially has a timezone offset).
 *
 * @param {Object} params - The parameters for the function.
 * @param {Date} params.date - The date to add minutes to.
 * @param {number} params.minutes - The number of minutes to add to the date.
 * @returns {Date} - The new date with the specified number of minutes to add or subtract
 */
export const addMinutesToIsoDate = ({ isoDate, minutes }) => {
  // Validate inputs
  if (!isoDate || typeof isoDate !== 'string' || typeof minutes !== 'number') {
    return null;
  }

  // Check for valid ISO date format with optional time zone offset (e.g., 2023-05-01T10:30:00Z)
  const isoDateRegex =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:Z|[+-]\d{2}:\d{2})?$/;

  if (!isoDateRegex.test(isoDate)) {
    return null;
  }

  // Split the isoDate to get the date/time portion and offset portion
  const [datePart, timePart] = isoDate.split('T');

  /**
   * Regex to match timezone offset (e.g., +05:30 or Z where there is no offset)
   */
  const TIMEZONE_OFFSETS_REGEX = /([+-]\d{2}:\d{2}|Z$)/;
  const timeSplit = timePart.match(TIMEZONE_OFFSETS_REGEX);

  let timePortion;
  let offset;

  if (timeSplit) {
    // If there's an offset (like +05:30 or Z)
    timePortion = timePart.slice(0, timeSplit.index);
    // eslint-disable-next-line prefer-destructuring
    offset = timeSplit[0];
  } else {
    // If no offset, assume UTC by default
    timePortion = timePart;
    offset = 'Z';
  }

  // Create a Date object assuming UTC if no offset, or use given offset
  const date = new Date(`${datePart}T${timePortion}Z`);

  // Add (or subtract if negative) minutes
  date.setUTCMinutes(date.getUTCMinutes() + minutes);

  // Format the new date, preserving the original offset
  const newDatePart = date.toISOString().slice(0, 19);

  return `${newDatePart}${offset}`;
};

export const filterAppointmentsWithinWeek = (appointments) => {
  if (!appointments || !appointments.length) return [];

  // Start of tomorrow
  const startOfTomorrow = new Date();
  startOfTomorrow.setHours(0, 0, 0, 0); // Start of today
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1); // Move to start of tomorrow

  // End of the week from tomorrow
  const nextWeek = new Date(startOfTomorrow);
  nextWeek.setDate(nextWeek.getDate() + 7);

  const filteredAppointments = appointments.filter((appointment) => {
    const appointmentDate = new Date(appointment.date);

    // Filter out appointments for today and include only those from tomorrow to the end of the week
    return appointmentDate >= startOfTomorrow && appointmentDate < nextWeek;
  });

  return filteredAppointments;
};

export const shouldShowAppointmentBookingOnPage = ({
  components,
  formId,
  pageNumber,
}) => {
  /**
   * Are conditional questions met?
   */
  const areConfigQuestionsAnswered = areConditionalQuestionsAnswered({
    formId,
    components,
    config: appointmentBookingConfig,
  });
  if (!areConfigQuestionsAnswered) return false;
  /**
   * Requires a postcode to have been entered
   */
  const postcodeComponent = getPostcodeComponent({ components });
  if (!postcodeComponent || !postcodeComponent?.input?.value) {
    return false;
  }
  const pagesWithLoader = [];
  Object.values(components).forEach((component) => {
    if (component.component.type === 'loader') {
      pagesWithLoader.push(component.component.page);
    }
  });
  /**
   * Requires the prior page to be a loader and have appointments
   */
  const isPreviousPageLoader = pagesWithLoader.includes(pageNumber - 1);

  return isPreviousPageLoader;
};

export const getSelectedClinicName = ({ appointments, selectedLocationId }) => {
  const appointment = appointments.find(
    (item) => item.location.locationId === selectedLocationId
  );

  return appointment ? appointment.location.name : null;
};

export const getSelectedClinicAddress = ({
  appointments,
  selectedLocationId,
}) => {
  const appointment = appointments.find(
    (item) => item.location.locationId === selectedLocationId
  );

  return appointment ? appointment.location.address : null;
};

export const getSelectedClinicMigrationStatus = ({
  appointments,
  selectedLocationId,
}) => {
  const appointment = appointments.find(
    (item) => item.location.locationId === selectedLocationId
  );

  return appointment ? appointment.location.isMigrated : null;
};

export const getSelectedClinicShopIdPRM = ({
  appointments,
  selectedLocationId,
}) => {
  const appointment = appointments.find(
    (item) => item.location.locationId === selectedLocationId
  );

  return appointment ? appointment.location.shopIdPRM : null;
};

/**
 * getSelectedAppointmentEndDate
 * @param {Array} param0.appointments - An array of appointment objects
 * @param {string} param0.selectedLocationId - The ID of the selected location
 * @param {string} param0.locale - The locale code of the form
 * @returns {string}
 *
 * Adds the duration to the selected appointment date and returns the end date as an iso string, or defaults to 60 minutes if duration is not available.
 */
export const getSelectedAppointmentEndDate = ({
  appointments,
  selectedLocationId,
  selectedDate,
}) => {
  if (!appointments || !selectedLocationId || !selectedDate) {
    return null;
  }

  const appointmentsForLocation = appointments.filter(
    (item) => item.location.locationId === selectedLocationId
  );

  const selectedAppointment = appointmentsForLocation.find(
    (appointment) => appointment.date === selectedDate
  );

  // Return the end date if available
  if (selectedAppointment?.endDate) {
    return selectedAppointment.endDate;
  }

  // Return a default of 60 minutes if one is not available
  return addMinutesToIsoDate({
    isoDate: selectedDate,
    minutes: selectedAppointment?.duration || 60,
  });
};

/**
 * getEventDataFromIframeMessageEvent
 *
 * We send messages from the appointment inner iframe using the format `messageName: { "type": "event", ... }`
 * This function extracts the event data from the message string.
 *
 * */
export const getEventDataFromIframeMessageEvent = (event) => {
  const message = event.data;
  const startIndex = message.indexOf('{');
  const eventDataJsonString = event.data.substring(startIndex);
  return JSON.parse(eventDataJsonString);
};
